import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Button,
    TextField,
    MenuItem,
    Switch,
    Tooltip,
    Snackbar,
    Alert,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import { Close, Add, Save, Delete } from "@mui/icons-material";
import {
    getSmsResponsables,
    createSmsResponsable,
    updateSmsResponsable,
    deleteSmsResponsable,
    getSucursales,
} from "../wa/api/wa_menu_Api";

export default function SmsResponsablesManager({ open, onClose }) {
    const [responsables, setResponsables] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
    const [adding, setAdding] = useState(false);
    const [editedRows, setEditedRows] = useState(new Set()); // Track de filas editadas
    const lastRowRef = useRef(null);

    useEffect(() => {
        if (open) {
            loadData();
        }
    }, [open]);

    const loadData = async () => {
        const [resp, sucs] = await Promise.all([
            getSmsResponsables(),
            getSucursales(),
        ]);
        setResponsables(resp || []);
        setSucursales(sucs || []);
        setEditedRows(new Set()); // Limpiar tracking al recargar
    };

    const scrollToLast = () => {
        setTimeout(() => {
            if (lastRowRef.current) {
                lastRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 100);
    };

    const handleAdd = () => {
        if (adding) return;
        setAdding(true);
        setResponsables((prev) => {
            const newResp = [
                ...prev,
                {
                    id: null,
                    sucursal: "DEFAULT",
                    nombre: "",
                    telefono: "+549",
                    activo: true,
                },
            ];
            scrollToLast();
            return newResp;
        });

        // Marcar la nueva fila como editada
        setTimeout(() => {
            setEditedRows((prev) => new Set([...prev, responsables.length]));
        }, 0);
    };

    const handleChange = (index, field, value) => {
        setResponsables((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });

        // Marcar fila como editada
        setEditedRows((prev) => new Set([...prev, index]));
    };

    const handleSave = async (resp, index) => {
        try {
            // Validaciones
            if (!resp.sucursal || !resp.nombre.trim() || !resp.telefono.trim()) {
                setSnack({ open: true, msg: "⚠️ Completá todos los campos", sev: "warning" });
                return;
            }

            const telefonoLimpio = resp.telefono.replace(/\s/g, "");
            if (!/^\+549\d{10}$/.test(telefonoLimpio)) {
                setSnack({
                    open: true,
                    msg: "⚠️ El teléfono debe tener 10 dígitos después de +549",
                    sev: "warning",
                });
                return;
            }

            const payload = {
                sucursal: resp.sucursal,
                nombre: resp.nombre.trim(),
                telefono: telefonoLimpio,
                activo: resp.activo ? 1 : 0,
            };

            if (resp.id) {
                await updateSmsResponsable(resp.id, payload);
            } else {
                await createSmsResponsable(payload);
            }

            setSnack({ open: true, msg: "✅ Responsable guardado", sev: "success" });
            setAdding(false);

            // Recargar sin reordenar
            const responsablesActualizados = await getSmsResponsables(); // 🆕 Cambié el nombre
            setResponsables(responsablesActualizados || []);

            // Limpiar tracking de esta fila
            setEditedRows((prev) => {
                const newSet = new Set(prev);
                newSet.delete(index);
                return newSet;
            });
        } catch (err) {
            console.error("Error al guardar:", err);
            setSnack({ open: true, msg: "❌ Error al guardar", sev: "error" });
        }
    };

    const handleDelete = async (id, index) => {
        if (!id) {
            // Si es nuevo (sin id), solo removerlo de la lista
            setResponsables((prev) => prev.filter((_, i) => i !== index));
            setAdding(false);
            setEditedRows((prev) => {
                const newSet = new Set(prev);
                newSet.delete(index);
                return newSet;
            });
            return;
        }

        if (!window.confirm("¿Eliminar este responsable?")) return;

        try {
            await deleteSmsResponsable(id);
            setSnack({ open: true, msg: "🗑️ Responsable eliminado", sev: "info" });

            const responsablesActualizados = await getSmsResponsables(); // 🆕 Cambié el nombre
            setResponsables(responsablesActualizados || []);
            setEditedRows(new Set());
        } catch {
            setSnack({ open: true, msg: "❌ Error al eliminar", sev: "error" });
        }
    };

    const handleTelefonoChange = (index, value) => {
        let tel = value;
        if (!tel.startsWith("+549")) {
            tel = "+549" + tel.replace(/^\+549/, "");
        }
        const digits = tel.slice(4).replace(/\D/g, "");
        const final = "+549" + digits.slice(0, 10);
        handleChange(index, "telefono", final);
    };

    const isRowEdited = (index) => editedRows.has(index);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6">📱 Responsables de SMS</Typography>
                    {editedRows.size > 0 && (
                        <Typography
                            variant="caption"
                            sx={{
                                backgroundColor: "#ff9800",
                                color: "white",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 2,
                                fontWeight: "bold"
                            }}
                        >
                            {editedRows.size} sin guardar
                        </Typography>
                    )}
                </Box>
                <Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        onClick={handleAdd}
                        sx={{ mr: 1 }}
                    >
                        Agregar
                    </Button>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                {responsables.length === 0 ? (
                    <Box sx={{ p: 4 }}>
                        <Typography color="text.secondary" align="center">
                            No hay responsables configurados. Hacé clic en "Agregar" para crear uno.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer
                        component={Paper}
                        sx={{
                            maxHeight: "60vh",
                            boxShadow: "none",
                        }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", width: "25%" }}>
                                        Sucursal
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", width: "25%" }}>
                                        Nombre
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", width: "30%" }}>
                                        Teléfono
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", width: "10%", textAlign: "center" }}>
                                        Activo
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", width: "10%", textAlign: "center" }}>
                                        Acciones
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {responsables.map((resp, i) => (
                                    <TableRow
                                        key={resp.id ?? `new-${i}`}
                                        ref={i === responsables.length - 1 ? lastRowRef : null}
                                        sx={{
                                            backgroundColor: isRowEdited(i) ? "#fff000" : "inherit",
                                            "&:hover": { backgroundColor: "#f5f5f5" },
                                        }}
                                    >
                                        {/* Sucursal */}
                                        <TableCell>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                value={resp.sucursal || "DEFAULT"}
                                                onChange={(e) => handleChange(i, "sucursal", e.target.value)}
                                                variant="standard"
                                            >
                                                {sucursales.map((suc) => (
                                                    <MenuItem key={suc.id} value={suc.id}>
                                                        {suc.id === "DEFAULT"
                                                            ? suc.nombre
                                                            : `${suc.id} - ${suc.nombre}`}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </TableCell>

                                        {/* Nombre */}
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={resp.nombre || ""}
                                                onChange={(e) => handleChange(i, "nombre", e.target.value)}
                                                placeholder="Ej: Juan Pérez"
                                                variant="standard"
                                            />
                                        </TableCell>

                                        {/* Teléfono */}
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={resp.telefono || "+549"}
                                                onChange={(e) => handleTelefonoChange(i, e.target.value)}
                                                placeholder="+5491154222222"
                                                variant="standard"
                                            />
                                        </TableCell>

                                        {/* Activo */}
                                        <TableCell align="center">
                                            <Switch
                                                checked={resp.activo || false}
                                                onChange={(e) => handleChange(i, "activo", e.target.checked)}
                                                size="small"
                                            />
                                        </TableCell>

                                        {/* Acciones */}
                                        <TableCell align="center">
                                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                                <Tooltip title={isRowEdited(i) ? "Guardar cambios" : "Sin cambios"}>
                                                    <span>
                                                        <IconButton
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleSave(resp, i)}
                                                            disabled={!isRowEdited(i)}
                                                        >
                                                            <Save fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Eliminar">
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleDelete(resp.id, i)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack({ ...snack, open: false })}
                    severity={snack.sev}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}